
import Vue from 'vue';
import { mapGetters } from 'vuex';
import moment from 'moment';
import BaseButton from '@/components/base/BaseButton.vue';
import { DbTimestamp } from '@run-diffusion/shared';

/**
 * Answers the question an admin actually arrives with: is my identity provider already moving people
 * around, or am I still in charge? Everything else on the page is a step toward changing that answer.
 */
export default Vue.extend({
	name: 'ProvisioningControlBanner',
	components: { BaseButton },
	props: {
		idpInControl: { type: Boolean, default: false },
		/** A SCIM token exists, so the Identity Provider has somewhere to push to. */
		scimConnected: { type: Boolean, default: false },
		groupsReceived: { type: Number, default: 0 },
		lastPushAt: { type: Object as () => DbTimestamp | null, default: null },
		lastDeployAt: { type: Object as () => DbTimestamp | null, default: null },
		lastDeployHadErrors: { type: Boolean, default: false },
		planChangedSinceLastDeploy: { type: Boolean, default: false },
		planStatus: { type: String, default: '' },
		planHasGaps: { type: Boolean, default: false },
		busy: { type: Boolean, default: false },
	},
	computed: {
		...mapGetters([
			'displayLabels',
		]),
		headline (): string {
			return this.idpInControl
				? 'Your identity provider does'
				: 'You do, manually, in RunDiffusion';
		},
		/**
		 * Describes the connection as it actually is. Asserting "connected and pushing groups" to an
		 * admin who has not generated a token yet, or whose Identity Provider has pushed nothing, reads
		 * as a broken page rather than a status.
		 */
		explanation (): string {
			const teamLabel = this.displayLabels.team;
			if (this.idpInControl) {
				return `Group membership in your identity provider decides who belongs to each ${teamLabel} and with which role. Members and roles cannot be edited by hand in RunDiffusion while this is on.`;
			}
			const untouched = `Every ${teamLabel} assignment you made by hand is untouched, and stays that way until you deploy.`;
			if (!this.scimConnected) {
				return `Your identity provider is not connected to provisioning yet. ${untouched}`;
			}
			if (!this.groupsReceived) {
				return `Your identity provider is connected but has not pushed any groups yet. ${untouched}`;
			}
			return `Your identity provider is connected and pushing groups, and none of it is applied. ${untouched}`;
		},
		groupsReceivedLabel (): string {
			if (!this.groupsReceived) return 'None yet';
			const at = this.lastPushAt ? moment(this.lastPushAt.toMillis()) : null;
			const when = at
				? (at.isSame(moment(), 'day') ? at.format('h:mm a') : at.format('MMM D'))
				: '';
			return when ? `${this.groupsReceived} · ${when}` : String(this.groupsReceived);
		},
		planStatusColor (): string {
			return this.planHasGaps ? 'var(--amber-500)' : 'var(--grey-100)';
		},
		lastDeployLabel (): string {
			if (!this.lastDeployAt) return 'None yet';
			const at = moment(this.lastDeployAt.toMillis());
			const when = at.isSame(moment(), 'day') ? at.format('h:mm a') : at.format('MMM D');
			if (this.lastDeployHadErrors) return `${when} · errors`;
			// A deploy did happen; it just predates the current plan. Saying so beats reporting no deploy.
			if (this.planChangedSinceLastDeploy) return `${when} · plan changed since`;
			return when;
		},
		lastDeployColor (): string {
			if (!this.lastDeployAt) return 'var(--grey-500)';
			if (this.lastDeployHadErrors) return 'var(--amber-500)';
			return this.planChangedSinceLastDeploy ? 'var(--amber-500)' : 'var(--grey-100)';
		},
	},
});
